<template>
  <div class="page-container">
    <PageHeader
      title="eTreasury Search"
      subtitle="Look up a challan in eTreasury by its GRN or by the department reference ID."
      icon="mdi-bank-outline"
    />

    <!-- Search -->
    <section class="surface pa-5 mb-5">
      <v-form ref="form" @submit.prevent="searchTransaction">
        <div class="et-mode">
          <span class="et-mode-label">Search by</span>
          <v-btn-toggle v-model="mode" mandatory dense class="et-toggle" @change="modeChanged">
            <v-btn value="GRN" small>GRN</v-btn>
            <v-btn value="DEPT" small>Department reference ID</v-btn>
          </v-btn-toggle>
        </div>

        <div class="et-row">
          <v-text-field
            id="et-query"
            v-model="query"
            outlined
            dense
            clearable
            :label="field.label"
            :placeholder="field.placeholder"
            :hint="field.hint"
            persistent-hint
            :counter="field.max"
            :rules="field.rules"
            :prepend-inner-icon="field.icon"
            class="et-input"
            @click:clear="clearResults"
          ></v-text-field>
          <v-btn type="submit" depressed class="btn-act btn-act--primary et-submit" :loading="loading" :disabled="!isSearchable">
            <v-icon left small>mdi-database-search-outline</v-icon>
            Search
          </v-btn>
        </div>
      </v-form>
    </section>

    <!-- Could not reach eTreasury, or it refused the request -->
    <div v-if="errorMsg" class="et-error mb-5">
      <v-icon small>mdi-alert-circle-outline</v-icon>
      <span>{{ errorMsg }}</span>
    </div>

    <!-- Results -->
    <section v-if="searched" class="surface">
      <div class="surface-toolbar">
        <div>
          <div class="surface-title">Results</div>
          <div class="surface-subtitle">{{ searchedLabel }}</div>
        </div>
        <v-btn depressed small class="btn-act btn-act--sm btn-act--neutral" @click="clearResults">
          <v-icon left small>mdi-close</v-icon>
          Clear
        </v-btn>
      </div>

      <div v-if="!transactions.length" class="empty-state">
        <v-icon size="52">mdi-file-search-outline</v-icon>
        <div class="cell-strong">No transaction found</div>
        <div>{{ notFoundText }}</div>
      </div>

      <div v-else class="pa-5 et-results">
        <article v-for="(t, i) in transactions" :key="i" class="et-card">
          <div class="et-card-head">
            <div class="et-card-id">
              <span class="et-card-key">GRN</span>
              <span class="et-card-grn">{{ t.grn || "—" }}</span>
            </div>
            <span class="status-pill" :class="'status-pill--' + t.tone">{{ t.statusLabel }}</span>
            <span class="et-card-amount">₹{{ money(t.amount) }}</span>
          </div>
          <div class="info-grid">
            <div v-for="f in t.fields" :key="f.label" class="info-item">
              <div class="info-label">{{ f.label }}</div>
              <div class="info-value" :class="{ 'et-mono': f.mono }">{{ f.value || "—" }}</div>
            </div>
          </div>
          <div v-if="t.remarks" class="et-remarks">
            <span class="info-label">Remarks</span>
            {{ t.remarks }}
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";
import PageHeader from "@/components/Common/PageHeader.vue";

// The two ways eTreasury can be searched.
const FIELDS = {
  GRN: {
    label: "Government Reference Number (GRN)",
    placeholder: "e.g. KL031621216202223M",
    hint: "18 letters and digits, as printed on the challan",
    icon: "mdi-identifier",
    max: 18,
    rules: [(v) => !v || /^[A-Za-z0-9]{18}$/.test(v.trim()) || "A GRN has exactly 18 letters and digits."],
  },
  DEPT: {
    label: "Department reference ID",
    placeholder: "e.g. SPARK16B3531",
    hint: "The reference the department sent to eTreasury with the payment",
    icon: "mdi-office-building-outline",
    max: 40,
    rules: [(v) => !v || v.trim().length <= 40 || "Up to 40 characters."],
  },
};

// Looks up one challan in eTreasury (/etreasury/transaction-details).
export default {
  name: "ETreasurySearch",
  components: { PageHeader },
  data() {
    return {
      mode: "GRN",
      query: "",
      loading: false,
      errorMsg: "",
      searched: false,
      searchedFor: "",
      searchedMode: "GRN",
      results: [],
    };
  },
  computed: {
    field() {
      return FIELDS[this.mode];
    },
    cleanQuery() {
      const q = (this.query || "").trim();
      return this.mode === "GRN" ? q.toUpperCase() : q;
    },
    isSearchable() {
      const q = this.cleanQuery;
      if (!q) return false;
      return this.mode === "GRN" ? /^[A-Z0-9]{18}$/.test(q) : q.length <= 40;
    },
    searchedLabel() {
      return (this.searchedMode === "GRN" ? "GRN " : "Department reference ID ") + this.searchedFor;
    },
    notFoundText() {
      return "eTreasury has no transaction for " + this.searchedLabel + ". Check the number and try again.";
    },
    // eTreasury answers with one object or a list; an entry with an error
    // (e.g. "Wrong GRN") means nothing was found. Keys vary in case.
    transactions() {
      const pick = (item, ...keys) => {
        for (const k of keys) if (item[k] != null && item[k] !== "") return item[k];
        return "";
      };
      return this.results
        .filter((item) => item && !item.error)
        .map((item) => {
          const status = String(pick(item, "status", "STATUS")).toUpperCase();
          return {
            grn: pick(item, "grn", "GRN"),
            amount: pick(item, "amount", "AMOUNT"),
            tone: status === "Y" ? "success" : status === "N" ? "danger" : "neutral",
            statusLabel: status === "Y" ? "Success" : status === "N" ? "Failed" : status || "Unknown",
            remarks: pick(item, "remarks", "REMARKS"),
            fields: [
              { label: "Party name", value: pick(item, "partyname", "PARTYNAME") },
              { label: "CIN", value: pick(item, "cin", "CIN"), mono: true },
              { label: "Bank reference", value: pick(item, "bankrefno", "BANKREFNO"), mono: true },
              { label: "Challan time", value: pick(item, "challantimestamp", "CHALLANTIMESTAMP") },
              { label: "Office code", value: pick(item, "officecode", "OFFICECODE", "OFFICEC ODE"), mono: true },
              { label: "Department ID", value: pick(item, "department_id", "DEPARTMENT_ID"), mono: true },
            ],
          };
        });
    },
  },
  methods: {
    modeChanged() {
      this.query = "";
      this.clearResults();
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    clearResults() {
      this.searched = false;
      this.results = [];
      this.errorMsg = "";
    },
    async searchTransaction() {
      if (!this.$refs.form.validate() || !this.isSearchable) return;
      const value = this.cleanQuery;
      this.loading = true;
      this.errorMsg = "";
      this.searched = false;
      try {
        const response = await axios({
          method: "POST",
          url: "/etreasury/transaction-details",
          headers: { token: localStorage.getItem("token") },
          data: this.mode === "GRN" ? { GRN: value } : { DEPARTMENT_ID: value },
        });
        if (response.data.status) {
          const data = response.data.data;
          this.results = Array.isArray(data) ? data : data ? [data] : [];
          this.searchedFor = value;
          this.searchedMode = this.mode;
          this.searched = true;
        } else {
          this.errorMsg = response.data.msg || "eTreasury did not return the transaction details.";
        }
      } catch (error) {
        console.error("eTreasury search error:", error);
        const r = error.response && error.response.data;
        this.errorMsg = (r && r.msg) || "Could not reach eTreasury. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    money(value) {
      return Number(value || 0).toLocaleString("en-IN");
    },
  },
};
</script>

<style scoped>
.et-mode {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.et-mode-label {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.et-toggle {
  border-radius: 10px !important;
  overflow: hidden;
}

.et-toggle .v-btn {
  text-transform: none !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
}

.et-toggle .v-btn.v-btn--active {
  background: #15803d !important;
  color: #ffffff !important;
}

.et-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
}

.et-input {
  flex: 1 1 340px;
  max-width: 560px;
}

.et-submit {
  height: 40px !important;
}

.et-error {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 14px;
  background: #fee2e2;
  color: #991b1b;
  font-size: 14px;
  font-weight: 600;
}

.et-error .v-icon {
  color: inherit !important;
  margin-top: 2px;
}

.et-results {
  display: grid;
  gap: 16px;
}

.et-card {
  padding: 16px 18px;
  border-radius: 14px;
  border: 1px solid var(--line);
  background: #fbfdfb;
}

.et-card-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
  margin-bottom: 14px;
}

.et-card-id {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.et-card-key {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
  color: var(--ink-muted);
}

.et-card-grn,
.et-mono {
  font-family: ui-monospace, "Cascadia Mono", Consolas, monospace;
  word-break: break-all;
}

.et-card-grn {
  font-size: 16px;
  font-weight: 700;
  color: var(--ink);
}

.et-card-amount {
  margin-left: auto;
  font-size: 20px;
  font-weight: 800;
  color: var(--brand-dark);
  font-variant-numeric: tabular-nums;
}

.et-remarks {
  margin-top: 12px;
  font-size: 14px;
  color: #334155;
}

.et-remarks .info-label {
  margin-right: 6px;
}

@media (max-width: 600px) {
  .et-submit {
    width: 100%;
  }

  .et-card-amount {
    margin-left: 0;
    width: 100%;
  }
}
</style>
